import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  Divider,
  Switch,
} from "@mui/material";
import { DoubleArrow } from "@mui/icons-material";
import { themes } from "services/constants";
import { CustomTextBox } from "components/CustomComponents";
import ColorLensIcon from "@mui/icons-material/ColorLens";
import { useForm } from "react-hook-form";
import { atleastOneCharacterWithSpace } from "services/constants/patterns";
import { useSelector } from "react-redux";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { renderTimeViewClock } from "@mui/x-date-pickers/timeViewRenderers";
import moment from "moment";
import {
  CustomSlider,
  CustomInputBox,
} from "components/CustomComponents/CustomSlider";
import dayjs from "dayjs";
import { customAPI } from "services/utils/CustomAPI";
import { CustomButton } from "components/CustomComponents";
import { CustomButtonWithBorder } from "components/CustomComponents/CustomButton";
import { showToast } from "services/utils/Status";
const presetColors = [
  themes?.softSkyBlue,
  themes?.mintGreen,
  themes?.lightGradient,
  themes?.lightPink,
  themes?.roseBlush,
  themes?.lavender,
  themes?.blueColor,
  themes?.babyCoral,
  themes?.paleYellow,
];

export const EditShiftPanel = ({
  onClose,
  reCallApi,
  editShiftDetails,
  setUpdateData,
  setGetId,
}) => {
  const [shiftName, setShiftName] = useState("");
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [selectedColor, setSelectedColor] = useState(
    editShiftDetails?.color_code
  );
  const { domain, token } = useSelector((state) => state.tokenReducer);
  const [startTimeFormat, setStartTimeFormat] = useState(null);
  const [shiftDurationFormat, setShiftDurationFormat] = useState(null);
  const [shiftDuration, setShiftDuration] = useState(null);
  const [errorHandling, setErrorHandling] = useState(false);

  const {
    register,
    setValue,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
    defaultValues: {
      trigger: true,
      button_loader: false,
      is_part_of_shift_hrs: false,
      break_duration: 0,
    },
  });

  const [policies, setPolicies] = useState({
    startEarly: 0,
    lateStart: 0,
    earlyEnd: 0,
    endLate: 0,
    overtimeLateStart: 0,
    overtimeEarlyEnd: 0,
  });

  const handlePolicyChange = (policy, value) => {
    setPolicies((prev) => ({
      ...prev,
      [policy]: parseInt(value, 10) || 0,
    }));
  };

  const sftWorkingHrs = dayjs(shiftDurationFormat).subtract(
    watch("break_duration"),
    "minute"
  );

  const calculatePreviewTimes = useCallback(() => {
    if (!startTime || !endTime) return null;

    const [startHour, startMin] = startTime.split(":").map(Number);
    const [endHour, endMin] = endTime.split(":").map(Number);

    const minStartTime = `${Math.max(
      0,
      startHour * 60 + startMin - policies.startEarly
    )}`;
    const maxStartTime = `${startHour * 60 + startMin + policies.lateStart}`;
    const minEndTime = `${Math.max(
      0,
      endHour * 60 + endMin - policies.earlyEnd
    )}`;
    const maxEndTime = `${endHour * 60 + endMin + policies.endLate}`;
    const overtimeLateStart = `${
      startHour * 60 + startMin + policies.overtimeLateStart
    }`;
    const overtimeEarlyEnd = `${Math.max(
      0,
      endHour * 60 + endMin - policies.overtimeEarlyEnd
    )}`;

    const formatTime = (minutes) => {
      const h = Math.floor(minutes / 60) % 24;
      const m = minutes % 60;
      return `${h.toString().padStart(2, "0")}:${m
        .toString()
        .padStart(2, "0")}`;
    };

    return {
      name: "Shift",
      minStartTime: formatTime(parseInt(minStartTime)),
      startTime,
      maxStartTime: formatTime(parseInt(maxStartTime)),
      minEndTime: formatTime(parseInt(minEndTime)),
      endTime,
      maxEndTime: formatTime(parseInt(maxEndTime)),
      overtimeLateStart: formatTime(parseInt(overtimeLateStart)),
      overtimeEarlyEnd: formatTime(parseInt(overtimeEarlyEnd)),
    };
  }, [
    endTime,
    policies.earlyEnd,
    policies.endLate,
    policies.lateStart,
    policies.overtimeEarlyEnd,
    policies.overtimeLateStart,
    policies.startEarly,
    startTime,
  ]);

  const previewData = calculatePreviewTimes();

  const handleName = (event) => {
    setShiftName(event.target.value);
    setValue("shift_name", event.target.value);
  };

  const handleStartTime = (newValue) => {
    if (newValue && newValue.format) {
      const timeWithDate = newValue ? newValue.toDate() : new Date();
      const time = newValue.format("HH:mm");
      setStartTime(time);
      let minTime = moment.utc(timeWithDate);
      setStartTimeFormat(
        dayjs()
          .set("hour", minTime._d.getHours())
          .set("minute", minTime._d.getMinutes())
          .set("second", minTime._d.getSeconds())
      );
    }
  };

  const handleShiftDuration = (newValue) => {
    if (newValue && newValue.format) {
      const timeWithDate = newValue ? newValue.toDate() : new Date();
      const time = newValue.format("HH:mm");
      setShiftDuration(time);
      let minTime = moment.utc(timeWithDate);
      setShiftDurationFormat(
        dayjs()
          .set("hour", minTime._d.getHours())
          .set("minute", minTime._d.getMinutes())
          .set("second", minTime._d.getSeconds())
      );
    }
  };

  const hanldePartOfShift = (event) => {
    const isChecked = event.target.checked;
    setValue("is_part_of_shift_hrs", isChecked);
  };

  useEffect(() => {
    if (shiftDuration && startTime) {
      const [startHour, startMinute] = startTime.split(":").map(Number);
      const [durHour, durMinute] = shiftDuration.split(":").map(Number);
      const start = new Date();
      start.setHours(startHour, startMinute, 0, 0);
      const end = new Date(
        start.getTime() + (durHour * 60 + durMinute) * 60 * 1000
      );
      const hours = end.getHours().toString().padStart(2, "0");
      const minutes = end.getMinutes().toString().padStart(2, "0");
      setEndTime(`${hours}:${minutes}`);
    }
  }, [startTime, shiftDuration]);

  const onSubmit = async () => {
    if (
      startTime &&
      startTime !== null &&
      startTime !== "Invalid Date" &&
      shiftDuration &&
      shiftDuration !== null &&
      shiftDuration !== "Invalid Date"
    ) {
      setValue("button_loader", true);
      const response = await customAPI(
        `${domain}administration/shiftroster/shift/shifts/${editShiftDetails?.id}/`,
        "PUT",
        {
          name: shiftName.trim(),
          start_time: previewData?.startTime,
          end_time: previewData?.endTime,
          min_start_time: previewData?.minStartTime,
          max_start_time: previewData?.maxStartTime,
          min_end_time: previewData?.minEndTime,
          max_end_time: previewData?.maxEndTime,
          ot_start_time: previewData?.startTime,
          ot_end_time: previewData?.endTime,
          ot_max_start_time: previewData?.overtimeLateStart,
          ot_min_end_time: previewData?.overtimeEarlyEnd,
          duration: shiftDuration,
          start_early: policies?.startEarly,
          start_delay: policies?.lateStart,
          end_early: policies?.earlyEnd,
          end_delay: policies?.endLate,
          ot_start_delay: policies?.overtimeLateStart,
          ot_end_early: policies?.overtimeEarlyEnd,
          color_code: selectedColor,
          is_break_allowed: true,
          break_duration: watch("break_duration"),
          is_break_part_of_shift: watch("is_part_of_shift_hrs"),
          shift_duration_hours: !watch("is_part_of_shift_hrs")
            ? sftWorkingHrs.format("HH:mm")
            : dayjs(shiftDurationFormat).format("HH:mm"),
          shift_working_hours: sftWorkingHrs.format("HH:mm"),
        }
      );
      if (response.ok) {
        setGetId(editShiftDetails?.id);
        showToast("success", "Shift updated successfully");
        setValue("button_loader", false);
        reCallApi(true);
        setUpdateData(false);
        onClose();
      }
    } else {
      setErrorHandling(true);
    }
    setValue("button_loader", false);
  };

  useEffect(() => {
    if (editShiftDetails && watch("trigger")) {
      setStartTime(editShiftDetails?.start_time);
      setEndTime(editShiftDetails?.end_time);
      setShiftDuration(editShiftDetails?.duration);
      setValue("shift_name", editShiftDetails?.name);
      setShiftName(editShiftDetails?.name);
      setShiftDurationFormat(
        editShiftDetails?.duration
          ? dayjs()
              .set("hour", editShiftDetails.duration.split(":")[0])
              .set("minute", editShiftDetails.duration.split(":")[1])
          : null
      );
      setStartTimeFormat(
        dayjs()
          .set("hour", editShiftDetails?.start_time.split(":")[0])
          .set("minute", editShiftDetails?.start_time.split(":")[1])
      );
      setPolicies({
        startEarly: editShiftDetails?.start_early,
        lateStart: editShiftDetails?.start_delay,
        earlyEnd: editShiftDetails?.end_early,
        endLate: editShiftDetails?.end_delay,
        overtimeLateStart: editShiftDetails?.ot_start_delay,
        overtimeEarlyEnd: editShiftDetails?.ot_end_early,
      });
      calculatePreviewTimes();
      setSelectedColor(editShiftDetails?.color_code);
      setValue(
        "is_part_of_shift_hrs",
        editShiftDetails?.is_break_part_of_shift
      );
      setValue("break_duration", editShiftDetails?.break_duration);
      setValue("trigger", false);
    }
  }, [calculatePreviewTimes, editShiftDetails, setValue, watch]);

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          borderBottom: `1px solid ${themes?.miliedLightGray}`,
          borderRadius: 0,
        }}
      >
        <Box
          display="flex"
          flexDirection="column"
          alignItems="flex-start"
          sx={{ padding: 1, backgroundColor: themes?.primary }}
        >
          <Box sx={{ ml: 1 }}>
            <IconButton
              onClick={onClose}
              sx={{
                color: themes?.grayColor,
                "&:hover": {
                  backgroundColor: themes?.whiteColor,
                },
              }}
            >
              <DoubleArrow />
            </IconButton>
          </Box>

          <Box sx={{ ml: 2.2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Edit Shift
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Edit working hours, assign employees, assign break and set rules
              for the shift
            </Typography>
          </Box>
        </Box>
      </Paper>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box sx={{ flexGrow: 1, overflow: "auto", p: 2 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 3,
            }}
          >
            <Box sx={{ ml: 2 }}>
              <Typography sx={{ fontWeight: 600, mb: 1 }}>
                Shift Name <span style={{ color: themes?.redColor }}>*</span>
              </Typography>
              <CustomTextBox
                selectedValue={shiftName}
                rules={{
                  ...register("shift_name", {
                    required: "Shift name is required",
                    minLength: {
                      value: 3,
                      message: "Minium length is 3 characters",
                    },
                    maxLength: {
                      value: 100,
                      message: "Maximum length is 100 characters",
                    },
                    validate: {
                      atleastOneCharacterWithSpaceReg: (value) => {
                        if (
                          atleastOneCharacterWithSpace.test(value) ||
                          value === ""
                        ) {
                          return true;
                        } else {
                          return "Atleast one alphabet required";
                        }
                      },
                      checkShiftUniqueName: async (fieldValue) => {
                        if (
                          fieldValue !== "" &&
                          shiftName !== editShiftDetails?.name
                        ) {
                          const response = await fetch(
                            `${domain}administration/shiftroster/shift/unique/?name/=${fieldValue.trim()}`,
                            {
                              method: "GET",
                              headers: {
                                "Content-Type": "application/json",
                                Authorization: `token ${token}`,
                              },
                            }
                          );
                          const resp = await response.json();
                          if (resp.exists) {
                            return "Shift name already exists";
                          }
                        }
                      },
                    },
                  }),
                }}
                updateDataFunction={handleName}
                placeHolder="Enter shift name"
                errorMessage={
                  errors?.shift_name?.message ? errors?.shift_name?.message : ""
                }
                textBoxError={errors.shift_name ? true : false}
              />
            </Box>
            <Box
              sx={{
                pointerEvents: editShiftDetails?.is_editable ? "auto" : "none",
                opacity: editShiftDetails?.is_editable ? 1 : 0.8,
                display: "flex",
                flexDirection: "column",
                gap: 3,
              }}
            >
              <Box sx={{ ml: 2 }}>
                <Typography sx={{ fontWeight: 600 }}>Shift Time</Typography>
                <Typography variant="body2" color="text.secondary">
                  Define the working hours, including start and end time for the
                  shift.
                </Typography>
              </Box>
              <Box>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr",
                    gap: 2,
                    ml: 2,
                  }}
                >
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Start Time
                      <span style={{ color: themes?.redColor }}>*</span>
                    </Typography>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DemoContainer components={["TimePicker"]}>
                        <div sx={{ width: 4 }}>
                          <TimePicker
                            ampm={false}
                            viewRenderers={{
                              hours: renderTimeViewClock,
                              minutes: renderTimeViewClock,
                              seconds: renderTimeViewClock,
                            }}
                            value={startTimeFormat || null}
                            slotProps={{
                              textField: {
                                size: "small",
                                width: 15,
                                sx: {
                                  "& .MuiOutlinedInput-root": {
                                    "&:hover fieldset": {
                                      borderColor: "green",
                                    },
                                  },
                                },
                              },
                            }}
                            onChange={(newValue) => {
                              handleStartTime(newValue);
                            }}
                          />
                        </div>
                      </DemoContainer>
                    </LocalizationProvider>
                    {(!startTime ||
                      startTime === null ||
                      (shiftDuration === null && startTime === "00:00") ||
                      startTime === "Invalid Date") &&
                    errorHandling ? (
                      <Typography
                        variant="body2"
                        color="error"
                        sx={{ fontSize: 12, paddingLeft: 0.5 }}
                      >
                        Start time is required
                      </Typography>
                    ) : null}
                  </Box>

                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Duration
                      <span style={{ color: themes?.redColor }}>*</span>
                    </Typography>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DemoContainer components={["TimePicker"]}>
                        <div sx={{ width: 4 }}>
                          <TimePicker
                            ampm={false}
                            viewRenderers={{
                              hours: renderTimeViewClock,
                              minutes: renderTimeViewClock,
                              seconds: renderTimeViewClock,
                            }}
                            value={shiftDurationFormat || null}
                            slotProps={{
                              textField: {
                                size: "small",
                                width: 15,
                              },
                            }}
                            onChange={(newValue) => {
                              handleShiftDuration(newValue);
                            }}
                          />
                        </div>
                      </DemoContainer>
                    </LocalizationProvider>
                    {(!shiftDuration ||
                      shiftDuration === null ||
                      shiftDuration === "Invalid Date") &&
                    errorHandling ? (
                      <Typography
                        variant="body2"
                        color="error"
                        sx={{ fontSize: 12, paddingLeft: 0.5 }}
                      >
                        Duration time is required
                      </Typography>
                    ) : null}
                  </Box>

                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                      End Time
                    </Typography>
                    <Card
                      sx={{
                        borderRadius: 2,
                        boxShadow: "none",
                        maxWidth: 200,
                      }}
                    >
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        gap={1}
                        sx={{
                          borderRadius: 2,
                          p: 1,
                          backgroundColor: themes?.miliedLightBlue,
                        }}
                      >
                        <Typography variant="subtitle1" fontWeight="700">
                          {endTime ?? "00:00"}
                        </Typography>
                      </Box>
                    </Card>
                  </Box>
                </Box>
              </Box>

              <Divider sx={{ borderColor: themes?.miliedLightGray }} />

              <Box
                sx={{
                  ml: 2,
                }}
              >
                <Typography sx={{ fontWeight: 600 }}>
                  Shift Start/End Policy
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Control rules for check-in and check-out, including grace
                  period and lateness
                </Typography>
              </Box>
              <Box>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 4,
                    ml: 2,
                  }}
                >
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Start Early{" "}
                      <span style={{ color: themes?.redColor }}>*</span>
                    </Typography>
                    <Box display="flex" alignItems="center" gap={2}>
                      <CustomSlider
                        value={policies.startEarly}
                        onChange={(e, value) =>
                          handlePolicyChange("startEarly", value)
                        }
                        min={0}
                        max={120}
                        step={1}
                        valueLabelDisplay="auto"
                        valueLabelFormat={(value) => `${value} min`}
                        sx={{ width: 150 }}
                      />

                      <CustomInputBox
                        name="startEarly"
                        value={policies.startEarly}
                        register={register}
                        errors={errors}
                        handlePolicyChange={handlePolicyChange}
                        rules={{ required: "Start early is required" }}
                      />

                      <Typography variant="body2">min</Typography>
                    </Box>
                  </Box>

                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                      Early End{" "}
                      <span style={{ color: themes?.redColor }}>*</span>
                    </Typography>
                    <Box display="flex" alignItems="center" gap={2}>
                      <CustomSlider
                        value={policies.earlyEnd}
                        onChange={(e, value) =>
                          handlePolicyChange("earlyEnd", value)
                        }
                        min={0}
                        max={120}
                        step={1}
                        valueLabelDisplay="auto"
                        valueLabelFormat={(value) => `${value} min`}
                        sx={{ width: 150 }}
                      />
                      <CustomInputBox
                        name="earlyEnd"
                        value={policies.earlyEnd}
                        register={register}
                        errors={errors}
                        handlePolicyChange={handlePolicyChange}
                        rules={{ required: "Early end is required" }}
                      />
                      <Typography variant="body2">min</Typography>
                    </Box>
                  </Box>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                      Late Start{" "}
                      <span style={{ color: themes?.redColor }}>*</span>
                    </Typography>
                    <Box display="flex" alignItems="center" gap={2}>
                      <CustomSlider
                        value={policies.lateStart}
                        onChange={(e, value) =>
                          handlePolicyChange("lateStart", value)
                        }
                        min={0}
                        max={120}
                        step={1}
                        valueLabelDisplay="auto"
                        valueLabelFormat={(value) => `${value} min`}
                        sx={{ width: 150 }}
                      />
                      <CustomInputBox
                        name="lateStart"
                        value={policies.lateStart}
                        register={register}
                        errors={errors}
                        handlePolicyChange={handlePolicyChange}
                        rules={{ required: "Late Start is required" }}
                      />
                      <Typography variant="body2">min</Typography>
                    </Box>
                  </Box>

                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                      End Late{" "}
                      <span style={{ color: themes?.redColor }}>*</span>
                    </Typography>
                    <Box display="flex" alignItems="center" gap={2}>
                      <CustomSlider
                        value={policies.endLate}
                        onChange={(e, value) =>
                          handlePolicyChange("endLate", value)
                        }
                        min={0}
                        max={120}
                        step={1}
                        valueLabelDisplay="auto"
                        valueLabelFormat={(value) => `${value} min`}
                        sx={{ width: 150 }}
                      />
                      <CustomInputBox
                        name="endLate"
                        value={policies.endLate}
                        register={register}
                        errors={errors}
                        handlePolicyChange={handlePolicyChange}
                        rules={{ required: "End late is required" }}
                      />
                      <Typography variant="body2">min</Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
              <Divider sx={{ borderColor: themes?.miliedLightGray }} />
              <Box
                sx={{
                  ml: 2,
                }}
              >
                <Typography sx={{ fontWeight: 600 }}>
                  OverTime Policy
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Configure extra working hours handling, approval rules, and
                  pay eligibility
                </Typography>
              </Box>
              <Box>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 4,
                    ml: 2,
                  }}
                >
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                      Late Start{" "}
                      <span style={{ color: themes?.redColor }}>*</span>
                    </Typography>
                    <Box display="flex" alignItems="center" gap={2}>
                      <CustomSlider
                        value={policies.overtimeLateStart}
                        onChange={(e, value) =>
                          handlePolicyChange("overtimeLateStart", value)
                        }
                        min={0}
                        max={120}
                        step={1}
                        valueLabelDisplay="auto"
                        valueLabelFormat={(value) => `${value} min`}
                        sx={{ width: 150 }}
                      />
                      <CustomInputBox
                        name="overtimeLateStart"
                        value={policies.overtimeLateStart}
                        register={register}
                        errors={errors}
                        handlePolicyChange={handlePolicyChange}
                        rules={{ required: "Late start is required" }}
                      />

                      <Typography variant="body2">min</Typography>
                    </Box>
                  </Box>

                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                      Early End{" "}
                      <span style={{ color: themes?.redColor }}>*</span>
                    </Typography>
                    <Box display="flex" alignItems="center" gap={2}>
                      <CustomSlider
                        value={policies.overtimeEarlyEnd}
                        onChange={(e, value) =>
                          handlePolicyChange("overtimeEarlyEnd", value)
                        }
                        min={0}
                        max={120}
                        step={1}
                        valueLabelDisplay="auto"
                        valueLabelFormat={(value) => `${value} min`}
                        sx={{ width: 150 }}
                      />
                      <CustomInputBox
                        name="overtimeEarlyEnd"
                        value={policies.overtimeEarlyEnd}
                        register={register}
                        errors={errors}
                        handlePolicyChange={handlePolicyChange}
                        rules={{ required: "Early end is required" }}
                      />
                      <Typography variant="body2">min</Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
              <Divider sx={{ borderColor: themes?.miliedLightGray }} />
              <Box
                sx={{
                  ml: 2,
                }}
              >
                <Typography sx={{ fontWeight: 600 }}>
                  Shift Break Policy
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Set rules for managing break duration in shift
                </Typography>
              </Box>
              <Box>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 4,
                    ml: 2,
                  }}
                >
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                      Break Duration{" "}
                      <span style={{ color: themes?.redColor }}>*</span>
                    </Typography>
                    <Box display="flex" alignItems="center" gap={2}>
                      <CustomSlider
                        value={watch("break_duration")}
                        onChange={(e, value) =>
                          setValue("break_duration", parseInt(value, 10) || 0)
                        }
                        min={0}
                        max={120}
                        step={1}
                        valueLabelDisplay="auto"
                        valueLabelFormat={(value) => `${value} min`}
                        sx={{ width: 150 }}
                      />
                      <CustomInputBox
                        name="break_duration"
                        value={watch("break_duration")}
                        register={register}
                        errors={errors}
                        handlePolicyChange={(e, value) => {
                          const breakValue = parseInt(value, 10) || 0;
                          setValue("break_duration", breakValue);
                          if (breakValue === 0) {
                            setValue("is_part_of_shift_hrs", false);
                          }
                        }}
                        rules={{ required: "Break duration is required" }}
                      />

                      <Typography variant="body2">min</Typography>
                    </Box>
                  </Box>

                  <Box sx={{ mt: 3.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Is Break part of shift hours{" "}
                      <Switch
                        checked={watch("is_part_of_shift_hrs")}
                        onChange={hanldePartOfShift}
                        inputProps={{ "aria-label": "controlled" }}
                        disabled={watch("break_duration") === 0}
                      />
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                      Shift Hours
                    </Typography>
                    <Card
                      sx={{
                        borderRadius: 2,
                        boxShadow: "none",
                      }}
                    >
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        gap={1}
                        sx={{
                          borderRadius: 2,
                          p: 1,
                          backgroundColor: themes?.primary,
                        }}
                      >
                        <Typography variant="subtitle1" fontWeight="700">
                          {!watch("is_part_of_shift_hrs")
                            ? sftWorkingHrs.isValid()
                              ? sftWorkingHrs.format("HH:mm")
                              : "00:00"
                            : dayjs(shiftDurationFormat).isValid()
                            ? dayjs(shiftDurationFormat).format("HH:mm")
                            : "00:00"}
                        </Typography>
                      </Box>
                    </Card>
                  </Box>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                      Shift Working Hours
                    </Typography>
                    <Card
                      sx={{
                        borderRadius: 2,
                        boxShadow: "none",
                      }}
                    >
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        gap={1}
                        sx={{
                          borderRadius: 2,
                          p: 1,
                          backgroundColor: themes?.primary,
                        }}
                      >
                        <Typography variant="subtitle1" fontWeight="700">
                          {sftWorkingHrs.isValid()
                            ? sftWorkingHrs.format("HH:mm")
                            : "00:00"}
                        </Typography>
                      </Box>
                    </Card>
                  </Box>
                </Box>
              </Box>
              <Divider sx={{ borderColor: themes?.miliedLightGray }} />

              <Box
                sx={{
                  ml: 2,
                }}
              >
                <Typography sx={{ fontWeight: 600 }}>Shift Color</Typography>
                <Typography variant="body2" color="text.secondary">
                  Assign a color to easily identify and differentiate this shift
                  in schedules
                </Typography>
              </Box>
              <Box>
                <Box display="flex" flexWrap="wrap" gap={2} sx={{ ml: 1.5 }}>
                  <IconButton
                    component="label"
                    sx={{
                      width: 50,
                      height: 50,
                      borderRadius: 1.5,
                      border: `2px solid ${themes?.lightGray}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      overflow: "hidden",
                      position: "relative",
                      cursor: "pointer",
                      backgroundColor: selectedColor,
                      "&:hover": {
                        backgroundColor: selectedColor,
                        opacity: 1,
                      },
                    }}
                  >
                    <input
                      type="color"
                      value={selectedColor}
                      onChange={(e) => setSelectedColor(e.target.value)}
                      style={{
                        position: "absolute",
                        inset: 0,
                        opacity: 0,
                        cursor: "pointer",
                      }}
                    />
                    <ColorLensIcon sx={{ fontSize: 35, mr: 1 }} />
                  </IconButton>

                  {presetColors.map((color) => (
                    <IconButton
                      key={color}
                      onClick={() => setSelectedColor(color)}
                      sx={{
                        width: 38,
                        height: 38,
                        mt: 0.7,
                        borderRadius: "50%",
                        backgroundColor: color,
                        border:
                          selectedColor === color
                            ? `2px solid ${themes?.headLine}`
                            : `2px solid ${themes?.whiteColor}`,
                        boxShadow:
                          selectedColor === color
                            ? "0 0 0 2px rgba(0,0,0,0.35)"
                            : "0 0 4px rgba(0,0,0,0.1)",
                        "&:hover": {
                          backgroundColor: color,
                          opacity: 1,
                        },
                      }}
                    />
                  ))}
                </Box>
              </Box>
              <Divider sx={{ borderColor: themes?.miliedLightGray }} />
            </Box>
            <Box
              sx={{
                ml: 2,
              }}
            >
              <Typography sx={{ fontWeight: 600 }}>Shift Preview</Typography>
              <Typography variant="body2" color="text.secondary">
                View a quick summary of the shift details before saving
              </Typography>
            </Box>

            <Box>
              <Paper
                elevation={0}
                sx={{
                  border: `1px solid ${themes?.miliedLightGray}`,
                  backgroundColor: themes?.whiteColor,
                  ml: 1.5,
                }}
              >
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: themes?.primary }}>
                        <TableCell
                          rowSpan={2}
                          sx={{
                            fontWeight: 600,
                            textAlign: "center",
                            borderRight: `1px solid ${themes?.miliedLightGray}`,
                          }}
                        >
                          Name
                        </TableCell>
                        <TableCell
                          colSpan={3}
                          sx={{
                            fontWeight: 600,
                            textAlign: "center",
                            borderRight: `1px solid ${themes?.miliedLightGray}`,
                          }}
                        >
                          Start Time
                        </TableCell>
                        <TableCell
                          colSpan={3}
                          sx={{
                            fontWeight: 600,
                            textAlign: "center",
                          }}
                        >
                          End Time
                        </TableCell>
                      </TableRow>
                      <TableRow
                        sx={{
                          backgroundColor: themes?.primary,
                          "& .MuiTableCell-root": {
                            textAlign: "center",
                            fontWeight: 600,
                            borderRight: `1px solid ${themes?.miliedLightGray}`,
                          },
                          "& .MuiTableCell-root:last-child": {
                            borderRight: "none",
                          },
                        }}
                      >
                        <TableCell>Min Start</TableCell>
                        <TableCell>Start</TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            borderRight: `1px solid ${themes?.miliedLightGray}`,
                          }}
                        >
                          Max Start
                        </TableCell>
                        <TableCell>Min End</TableCell>
                        <TableCell>End</TableCell>
                        <TableCell>Max End</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      <TableRow
                        sx={{
                          backgroundColor: selectedColor,
                          "& .MuiTableCell-root": {
                            textAlign: "center",
                            borderRight: `1px solid ${themes?.miliedLightGray}`,
                          },
                          "& .MuiTableCell-root:last-child": {
                            borderRight: "none",
                          },
                        }}
                      >
                        <TableCell
                          sx={{
                            fontSize: "0.875rem",
                            borderRight: `1px solid ${themes?.miliedLightGray}`,
                          }}
                        >
                          {previewData?.name || "Shift"}
                        </TableCell>
                        <TableCell>
                          {previewData?.minStartTime ?? "00:00"}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 500 }}>
                          {previewData?.startTime ?? "00:00"}
                        </TableCell>
                        <TableCell
                          sx={{
                            borderRight: `1px solid ${themes?.miliedLightGray}`,
                          }}
                        >
                          {previewData?.maxStartTime ?? "00:00"}
                        </TableCell>
                        <TableCell>
                          {previewData?.minEndTime ?? "00:00"}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 500 }}>
                          {previewData?.endTime ?? "00:00"}
                        </TableCell>
                        <TableCell>
                          {previewData?.maxEndTime ?? "00:00"}
                        </TableCell>
                      </TableRow>

                      <TableRow
                        sx={{
                          "& .MuiTableCell-root": {
                            textAlign: "center",
                            borderRight: `1px solid ${themes?.miliedLightGray}`,
                          },
                          "& .MuiTableCell-root:last-child": {
                            borderRight: "none",
                          },
                        }}
                      >
                        <TableCell
                          sx={{
                            fontSize: "0.875rem",
                          }}
                        >
                          Overtime
                        </TableCell>
                        <TableCell></TableCell>
                        <TableCell>
                          {previewData?.startTime ?? "00:00"}
                        </TableCell>
                        <TableCell>
                          {previewData?.overtimeLateStart ?? "00:00"}
                        </TableCell>
                        <TableCell>
                          {" "}
                          {previewData?.overtimeEarlyEnd ?? "00:00"}
                        </TableCell>
                        <TableCell>{previewData?.endTime ?? "00:00"}</TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Box>

            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: "flex-end",
              }}
            >
              <CustomButtonWithBorder
                actionName="Cancel"
                typeName="button"
                actionFuntion={() => onClose()}
              />
              <CustomButton
                actionFuntion={handleSubmit(onSubmit)}
                actionName="Update"
                typeName="submit"
                disableAction={watch("button_loader")}
              />
            </Box>
          </Box>
        </Box>
      </form>
    </Box>
  );
};
